import type { ReactNode } from 'react'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ApiClientError } from '../api/http'
import { Button, Card, ChannelDot, ChannelPill, Icon, I, MOBILE_SINGLE_COLUMN, Pill, TEXT_WRAP_STYLE, Thumb, mobileButtonStyle } from '../components/ui'
import { getHomeDashboard } from '../dashboard/dashboardApi'
import type { ChannelConnectionStatus, DashboardActionItem, DashboardActionTarget, DashboardSeverity, HomeDashboard, InventoryRisk, MoneyTotal, Provider, RecentOrder, TrendPoint } from '../dashboard/types'
import { useIsMobileViewport } from '../hooks/useIsMobileViewport'

export type HomeDashboardTarget = 'orders' | 'products' | 'channels' | 'channel-inventory-variance' | 'inventory' | 'team'

type PillTone = 'neutral' | 'success' | 'warning' | 'danger' | 'info' | 'brand' | 'coral'
type TrendMetric = 'revenue' | 'orders'

interface HomePageProps {
  accessToken: string
  onNavigate?: (target: HomeDashboardTarget) => void
  onOpenProduct?: (productId: number) => void
}

interface DashboardStat {
  label: string
  value: string
  sub: string
  tone: PillTone
  icon: ReactNode
}

interface DashboardLoadState {
  requestKey: string
  data: HomeDashboard | null
  error: unknown | null
}

const ACTION_TARGETS: Record<DashboardActionTarget, HomeDashboardTarget> = {
  ORDERS: 'orders',
  PRODUCTS: 'products',
  CHANNELS: 'channels',
  INVENTORY: 'inventory',
}

const PROVIDER_DISPLAY: Record<Provider, string> = {
  ZID: 'Zid',
  SALLA: 'Salla',
  AMAZON: 'Amazon',
  TRENDYOL: 'Trendyol',
  NOON: 'Noon',
}

function numberLocale(locale: string) {
  return locale.startsWith('ar') ? 'ar-SA' : 'en-US'
}

function formatNumber(value: number, locale: string): string {
  return new Intl.NumberFormat(numberLocale(locale), { maximumFractionDigits: 0 }).format(value)
}

function formatMoney(value: number, locale: string, currency: string): string {
  return `${formatNumber(value, locale)} ${currency}`
}

function formatCompactNumber(value: number, locale: string): string {
  return new Intl.NumberFormat(numberLocale(locale), { notation: 'compact', maximumFractionDigits: 1 }).format(value)
}

function formatCompactMoney(value: number, locale: string, currency: string): string {
  return `${formatCompactNumber(value, locale)} ${currency}`
}

function moneyValue(total: MoneyTotal): number {
  return typeof total.total === 'number' ? total.total : Number(total.total ?? 0)
}

function formatMoneyTotals(totals: MoneyTotal[], locale: string, fallbackCurrency: string): string {
  if (totals.length === 0) return formatMoney(0, locale, fallbackCurrency)
  return totals
    .slice(0, 2)
    .map(total => formatMoney(moneyValue(total), locale, total.currency || fallbackCurrency))
    .join(' · ')
}

function totalRevenue(totals: MoneyTotal[]): number {
  return totals.reduce((sum, total) => sum + moneyValue(total), 0)
}

function riskTone(risk: InventoryRisk): PillTone {
  return risk === 'HIGH' ? 'danger' : 'warning'
}

function severityTone(severity: DashboardSeverity): PillTone {
  if (severity === 'DANGER') return 'danger'
  if (severity === 'WARNING') return 'warning'
  return 'info'
}

function channelStatusTone(status: ChannelConnectionStatus): PillTone {
  return status === 'CONNECTED' ? 'success' : status === 'PENDING' ? 'info' : 'warning'
}

function orderStatusTone(status: RecentOrder['status']): PillTone {
  if (status === 'COMPLETED') return 'success'
  if (status === 'CANCELLED') return 'danger'
  if (status === 'UNKNOWN') return 'neutral'
  return 'info'
}

function actionIcon(action: DashboardActionItem): ReactNode {
  if (action.type === 'CHANNEL_ATTENTION' || action.type === 'ZOHO_CONNECTION_ATTENTION') return I.alert
  if (action.type === 'LOW_STOCK') return I.warehouse
  if (action.type === 'CHANNEL_INVENTORY_VARIANCE') return I.trendD
  if (action.type === 'UNMAPPED_LISTINGS') return I.package
  return I.cart
}

function actionDestination(action: DashboardActionItem): HomeDashboardTarget {
  if (action.type === 'CHANNEL_INVENTORY_VARIANCE') return 'channel-inventory-variance'
  return ACTION_TARGETS[action.target]
}

function errorMessage(error: unknown, t: (key: string) => string): string {
  if (error instanceof ApiClientError) {
    return t(`home.errors.codes.${error.code}`)
  }
  return t('home.errors.codes.REQUEST_FAILED')
}

function SectionHeader({ title, subtitle, action }: { title: string; subtitle?: string; action?: ReactNode }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 14, marginBottom: 16 }}>
      <div>
        <h3 style={{ margin: 0, fontSize: 16, fontWeight: 800, color: 'var(--brand-900)', letterSpacing: '-0.01em' }}>{title}</h3>
        {subtitle && <p style={{ margin: '3px 0 0', color: 'var(--fg-subtle)', fontSize: 12.5, lineHeight: 1.45 }}>{subtitle}</p>}
      </div>
      {action}
    </div>
  )
}

function MetricRow({ label, value }: { label: string; value: string }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, paddingBlock: 9, borderTop: '1px solid var(--border-subtle)' }}>
      <span style={{ color: 'var(--fg-subtle)', fontSize: 12 }}>{label}</span>
      <strong style={{ color: 'var(--fg)', fontSize: 12.5, fontWeight: 800 }}>{value}</strong>
    </div>
  )
}

function EmptyState({ title, body, action }: { title: string; body: string; action?: ReactNode }) {
  return (
    <div style={{ border: '1px dashed var(--border-default)', borderRadius: 14, paddingBlock: 18, paddingInline: 16, background: 'var(--ink-50)', color: 'var(--fg-subtle)', fontSize: 13, lineHeight: 1.45 }}>
      <strong style={{ display: 'block', color: 'var(--fg)', fontSize: 13.5 }}>{title}</strong>
      <span style={{ display: 'block', marginTop: 4 }}>{body}</span>
      {action && <div style={{ marginTop: 12 }}>{action}</div>}
    </div>
  )
}

function DashboardStatCard({ stat }: { stat: DashboardStat }) {
  return (
    <Card padding={18} style={{ minHeight: 126 }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 10 }}>
        <div style={{ width: 36, height: 36, borderRadius: 12, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', background: 'var(--brand-100)', color: 'var(--brand-700)' }}>
          <Icon size={18}>{stat.icon}</Icon>
        </div>
        <Pill tone={stat.tone} size="sm" dot>{stat.sub}</Pill>
      </div>
      <div style={{ marginTop: 14, fontSize: 11, fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--fg-subtle)' }}>
        {stat.label}
      </div>
      <div style={{ marginTop: 4, fontSize: 24, lineHeight: 1, fontWeight: 800, color: 'var(--brand-900)', letterSpacing: '-0.02em' }}>
        {stat.value}
      </div>
    </Card>
  )
}

export function HomePage({ accessToken, onNavigate, onOpenProduct }: HomePageProps) {
  const { t, i18n } = useTranslation()
  const isMobile = useIsMobileViewport()
  const locale = i18n.language
  const currency = t('home.currency')
  const [refreshNonce, setRefreshNonce] = useState(0)
  const [trendMetric, setTrendMetric] = useState<TrendMetric>('revenue')
  const [selectedTrendDate, setSelectedTrendDate] = useState<string | null>(null)
  const requestKey = `${accessToken}:${refreshNonce}`
  const [loadState, setLoadState] = useState<DashboardLoadState>({ requestKey: '', data: null, error: null })

  const navigate = (target: HomeDashboardTarget) => onNavigate?.(target)

  useEffect(() => {
    let cancelled = false

    getHomeDashboard(accessToken)
      .then(response => {
        if (cancelled) return
        setLoadState({ requestKey, data: response, error: null })
      })
      .catch(fetchError => {
        if (cancelled) return
        setLoadState({ requestKey, data: null, error: fetchError })
      })

    return () => {
      cancelled = true
    }
  }, [accessToken, requestKey])

  const data = loadState.requestKey === requestKey ? loadState.data : null
  const error = loadState.requestKey === requestKey ? loadState.error : null
  const loading = loadState.requestKey !== requestKey

  const heroButtonMobileStyle = isMobile
    ? { width: '100%', justifyContent: 'center', whiteSpace: 'normal' as const, textAlign: 'center' as const, minWidth: 0 }
    : { minWidth: 0 }

  const renderHero = () => (
    <Card padding={0} style={{ overflow: 'hidden', background: 'var(--grad-brand)', color: 'var(--fg-on-brand)', border: 'none', position: 'relative', minWidth: 0 }}>
      <div style={{ position: 'absolute', insetBlockStart: -80, insetInlineEnd: -80, width: 210, height: 210, borderRadius: '50%', background: 'rgba(255,255,255,0.14)' }} />
      <div style={{ position: 'relative', paddingBlock: isMobile ? 22 : 30, paddingInline: isMobile ? 18 : 30, boxSizing: 'border-box', minWidth: 0, maxWidth: '100%' }}>
        <Pill tone="coral" size="sm" style={{ background: 'rgba(255,255,255,0.16)', color: 'var(--fg-on-brand)' }}>{t('home.hero.eyebrow')}</Pill>
        <h2 data-testid="home-hero-title" style={{ margin: '18px 0 0', maxWidth: isMobile ? '100%' : 680, fontSize: isMobile ? 24 : 32, lineHeight: isMobile ? 1.14 : 1.08, fontWeight: 800, letterSpacing: '-0.03em', overflowWrap: 'break-word' }}>
          {t('home.hero.title')}
        </h2>
        <p style={{ margin: '10px 0 0', maxWidth: isMobile ? '100%' : 560, color: 'rgba(255,255,255,0.78)', fontSize: isMobile ? 14 : 15.5, lineHeight: 1.55, overflowWrap: 'break-word' }}>
          {t('home.hero.body')}
        </p>
        <div data-testid="home-hero-actions" style={{ marginTop: 24, display: isMobile ? 'grid' : 'flex', gridTemplateColumns: isMobile ? 'minmax(0, 1fr)' : undefined, flexWrap: isMobile ? undefined : 'wrap', gap: 10, alignItems: isMobile ? 'stretch' : 'center', width: '100%', minWidth: 0 }}>
          <Button kind="onDark" size="md" icon={I.link} onClick={() => navigate('channels')} style={heroButtonMobileStyle}>{t('home.hero.primaryAction')}</Button>
          <Button kind="ghost" size="md" icon={I.package} onClick={() => navigate('products')} style={{ color: 'var(--fg-on-brand)', background: 'rgba(255,255,255,0.12)', ...heroButtonMobileStyle }}>
            {t('home.hero.secondaryAction')}
          </Button>
          <span style={{ color: 'rgba(255,255,255,0.72)', fontSize: 12.5, fontWeight: 700, justifySelf: isMobile ? 'center' : undefined, textAlign: isMobile ? 'center' : undefined, overflowWrap: 'break-word', minWidth: 0 }}>{t('home.hero.window')}</span>
        </div>
      </div>
    </Card>
  )

  if (loading && data == null) {
    return (
      <div style={{ display: 'grid', gap: 18, paddingBottom: 48 }}>
        {renderHero()}
        <Card padding={22}>
          <SectionHeader title={t('home.loading.title')} subtitle={t('home.loading.body')} action={<Pill tone="brand" size="sm">{t('home.loading.badge')}</Pill>} />
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 180px), 1fr))', gap: 14 }}>
            {[0, 1, 2, 3].map(index => (
              <div key={index} style={{ height: 104, borderRadius: 14, background: 'var(--ink-50)', border: '1px solid var(--border-subtle)' }} />
            ))}
          </div>
        </Card>
      </div>
    )
  }

  if (error && data == null) {
    return (
      <div style={{ display: 'grid', gap: 18, paddingBottom: 48 }}>
        {renderHero()}
        <Card padding={22}>
          <SectionHeader title={t('home.errors.title')} subtitle={errorMessage(error, t)} />
          <Button kind="primary" size="sm" icon={I.refresh} onClick={() => setRefreshNonce(value => value + 1)}>{t('home.errors.retry')}</Button>
        </Card>
      </div>
    )
  }

  if (data == null) return null

  const stats: DashboardStat[] = [
    { label: t('home.stats.orders'), value: formatNumber(data.overview.ordersLast24h, locale), sub: t('home.stats.ordersSub', { count: data.overview.activeSellingChannelsLast24h }), tone: 'success', icon: I.cart },
    { label: t('home.stats.revenue'), value: formatMoneyTotals(data.overview.revenueLast24h, locale, currency), sub: t('home.stats.revenueSub'), tone: 'brand', icon: I.trend },
    {
      label: t('home.stats.connectedChannels'),
      value: `${formatNumber(data.overview.connectedChannels, locale)}/${formatNumber(data.overview.totalChannels, locale)}`,
      sub: t('home.stats.connectedChannelsSub', { connected: data.overview.connectedChannels, total: data.overview.totalChannels }),
      tone: data.overview.connectedChannels === data.overview.totalChannels && data.overview.totalChannels > 0 ? 'success' : 'info',
      icon: I.link,
    },
    { label: t('home.stats.actionQueue'), value: formatNumber(data.overview.actionItemCount, locale), sub: t('home.stats.actionQueueSub'), tone: data.overview.actionItemCount > 0 ? 'warning' : 'success', icon: I.zap },
  ]

  const trendValue = (point: TrendPoint) => trendMetric === 'revenue' ? totalRevenue(point.revenue) : point.orderCount
  const maxTrendValue = Math.max(1, ...data.trend.map(trendValue))
  const sevenDayRevenue = data.trend.reduce((sum, point) => sum + totalRevenue(point.revenue), 0)
  const sevenDayOrders = data.trend.reduce((sum, point) => sum + point.orderCount, 0)
  const selectedTrendPoint = data.trend.find(point => point.date === selectedTrendDate) ?? data.trend[data.trend.length - 1]
  const bestTrendPoint = data.trend.reduce((best, point) => trendValue(point) > trendValue(best) ? point : best, data.trend[0])
  const trendDayLabel = (point: TrendPoint) => new Intl.DateTimeFormat(numberLocale(locale), { weekday: 'short' }).format(new Date(`${point.date}T00:00:00Z`))

  return (
    <div style={{ display: 'grid', gap: 18, paddingBottom: 48 }}>
      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(min(100%, 320px), 1fr))', gap: 18 }}>
        {renderHero()}

        <Card padding={22}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 14 }}>
            <div>
              <div style={{ fontSize: 13, fontWeight: 800, color: 'var(--brand-900)' }}>{t('home.coverage.title')}</div>
              <div style={{ marginTop: 5, color: 'var(--fg-subtle)', fontSize: 12 }}>{t('home.coverage.subtitle')}</div>
            </div>
            <div style={{ width: 82, height: 82, borderRadius: '50%', display: 'grid', placeItems: 'center', background: 'var(--brand-100)', border: '8px solid var(--bg-card)', boxShadow: '0 0 0 1px var(--border-subtle)' }}>
              <span style={{ color: 'var(--brand-700)', fontSize: 22, fontWeight: 800 }}>{formatNumber(data.coverage.totalProducts, locale)}</span>
            </div>
          </div>
          <div style={{ marginTop: 18 }}>
            <MetricRow label={t('home.coverage.totalProducts')} value={formatNumber(data.coverage.totalProducts, locale)} />
            <MetricRow label={t('home.coverage.listedProducts')} value={formatNumber(data.coverage.listedProducts, locale)} />
            <MetricRow label={t('home.coverage.stockCoveredProducts')} value={formatNumber(data.coverage.stockCoveredProducts, locale)} />
            <MetricRow label={t('home.coverage.connectedChannels')} value={`${formatNumber(data.coverage.connectedChannels, locale)}/${formatNumber(data.coverage.totalChannels, locale)}`} />
          </div>
        </Card>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(min(100%, 180px), 1fr))', gap: 14 }}>
        {stats.map(stat => <DashboardStatCard key={stat.label} stat={stat} />)}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(min(100%, 360px), 1fr))', gap: 18, alignItems: 'start' }}>
        <div style={{ display: 'grid', gap: 18 }}>
          <Card padding={22}>
            <SectionHeader title={t('home.actionQueue.title')} subtitle={t('home.actionQueue.subtitle')} />
            {data.actionItems.length === 0 ? (
              <EmptyState title={t('home.actionQueue.empty.title')} body={t('home.actionQueue.empty.body')} />
            ) : (
              <div style={{ display: 'grid', gap: 10 }}>
                {data.actionItems.map(action => {
                  const target = actionDestination(action)
                  return (
                    <div key={action.type} style={{ display: 'grid', gridTemplateColumns: isMobile ? '40px minmax(0, 1fr)' : '40px minmax(0, 1fr) auto', gap: 12, alignItems: 'center', paddingBlock: 12, paddingInline: 12, border: '1px solid var(--border-subtle)', borderRadius: 14, background: 'var(--ink-50)' }}>
                      <div style={{ width: 40, height: 40, borderRadius: 12, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', color: 'var(--brand-700)', background: 'var(--bg-card)' }}>
                        <Icon size={18}>{actionIcon(action)}</Icon>
                      </div>
                      <div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                          <strong style={{ color: 'var(--fg)', fontSize: 13.5 }}>{t(`home.actionQueue.items.${action.type}.title`, { count: action.count })}</strong>
                          <Pill tone={severityTone(action.severity)} size="sm" dot>{t(`home.actionQueue.items.${action.type}.badge`, { count: action.count })}</Pill>
                        </div>
                        <p style={{ margin: '4px 0 0', color: 'var(--fg-subtle)', fontSize: 12.5, lineHeight: 1.45 }}>{t(`home.actionQueue.items.${action.type}.body`, { count: action.count })}</p>
                      </div>
                      <Button kind="ghost" size="sm" icon={i18n.dir() === 'rtl' ? I.arrowL : I.arrowR} onClick={() => navigate(target)} style={{ gridColumn: isMobile ? '1 / -1' : undefined, justifyContent: isMobile ? 'center' : undefined }}>{t(`home.actionQueue.items.${action.type}.cta`)}</Button>
                    </div>
                  )
                })}
              </div>
            )}
          </Card>

          <Card padding={22}>
            <SectionHeader
              title={t('home.trend.title')}
              subtitle={t('home.trend.subtitle')}
              action={(
                <div role="group" aria-label={t('home.trend.metricToggle')} style={{ display: 'inline-grid', gridTemplateColumns: '1fr 1fr', gap: 4, padding: 4, borderRadius: 999, background: 'var(--ink-50)', border: '1px solid var(--border-subtle)' }}>
                  {(['revenue', 'orders'] as const).map(metric => {
                    const active = trendMetric === metric
                    return (
                      <button
                        key={metric}
                        type="button"
                        aria-pressed={active}
                        onClick={() => setTrendMetric(metric)}
                        style={{ border: 0, borderRadius: 999, paddingBlock: 6, paddingInline: 12, background: active ? 'var(--brand-100)' : 'transparent', color: active ? 'var(--brand-700)' : 'var(--fg-subtle)', fontFamily: 'inherit', fontSize: 12.5, fontWeight: 800, cursor: 'pointer', whiteSpace: 'nowrap' }}
                      >
                        {t(`home.trend.metrics.${metric}`)}
                      </button>
                    )
                  })}
                </div>
              )}
            />
            <div style={{ overflowX: isMobile ? 'auto' : undefined }}>
              <div style={{ display: 'grid', gridTemplateColumns: `repeat(${data.trend.length}, minmax(${isMobile ? '58px' : '0'}, 1fr))`, gap: 10, alignItems: 'end', minHeight: 178, minWidth: isMobile ? 460 : undefined }}>
                {data.trend.map((point: TrendPoint) => {
                  const revenue = totalRevenue(point.revenue)
                  const metricValue = trendValue(point)
                  const height = metricValue === 0 ? 12 : Math.max(36, Math.round((metricValue / maxTrendValue) * 124))
                  const dayLabel = trendDayLabel(point)
                  const valueLabel = trendMetric === 'revenue' ? formatCompactMoney(revenue, locale, currency) : formatNumber(point.orderCount, locale)
                  const selected = selectedTrendPoint?.date === point.date
                  return (
                    <button key={point.date} type="button" aria-label={t('home.trend.dayAria', { day: dayLabel, orders: formatNumber(point.orderCount, locale), revenue: formatMoney(revenue, locale, currency) })} onClick={() => setSelectedTrendDate(point.date)} style={{ display: 'grid', gap: 8, alignItems: 'end', border: 0, background: 'transparent', padding: 0, fontFamily: 'inherit', cursor: 'pointer', minWidth: 0 }}>
                      <div style={{ display: 'grid', alignItems: 'end', height: 128 }}>
                        <div style={{ height, borderRadius: '10px 10px 6px 6px', background: metricValue === 0 ? 'var(--ink-100)' : 'linear-gradient(180deg, var(--brand-500), var(--brand-700))', boxShadow: selected ? '0 0 0 3px var(--brand-100), 0 10px 18px rgba(37,87,232,0.22)' : metricValue === 0 ? undefined : '0 8px 16px rgba(37,87,232,0.18)' }} />
                      </div>
                      <div style={{ textAlign: 'center', minWidth: 0 }}>
                        <div style={{ fontSize: 12, fontWeight: 800, color: 'var(--fg)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{valueLabel}</div>
                        <div style={{ fontSize: 11, color: 'var(--fg-subtle)' }}>{dayLabel}</div>
                      </div>
                    </button>
                  )
                })}
              </div>
            </div>
            <div style={{ marginTop: 14, display: 'flex', gap: 10, flexWrap: 'wrap', color: 'var(--fg-subtle)', fontSize: 12 }}>
              <span>{t('home.trend.summaryRevenue', { amount: formatMoney(sevenDayRevenue, locale, currency) })}</span>
              <span>{t('home.trend.summaryOrders', { count: formatNumber(sevenDayOrders, locale) })}</span>
              {bestTrendPoint && <span>{t('home.trend.bestDay', { day: trendDayLabel(bestTrendPoint) })}</span>}
            </div>
            {selectedTrendPoint && (
              <div style={{ marginTop: 10, paddingBlock: 9, paddingInline: 12, borderRadius: 12, background: 'var(--ink-50)', color: 'var(--fg-muted)', fontSize: 12.5, fontWeight: 700 }}>
                {t('home.trend.selectedDay', { day: trendDayLabel(selectedTrendPoint), orders: formatNumber(selectedTrendPoint.orderCount, locale), revenue: formatMoney(totalRevenue(selectedTrendPoint.revenue), locale, currency) })}
              </div>
            )}
          </Card>

          <Card padding={0}>
            <div style={{ paddingBlock: 16, paddingInline: 22, borderBottom: '1px solid var(--border-subtle)', display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'center' }}>
              <div>
                <div style={{ fontSize: 15, fontWeight: 800, color: 'var(--brand-900)' }}>{t('home.recent.title')}</div>
                <div style={{ fontSize: 12, color: 'var(--fg-subtle)', marginTop: 2 }}>{t('home.recent.subtitle')}</div>
              </div>
              <Button kind="ghost" size="sm" icon={I.arrowR} onClick={() => navigate('orders')} style={mobileButtonStyle(isMobile)}>{t('home.recent.viewAll')}</Button>
            </div>
            {data.recentOrders.length === 0 ? (
              <div style={{ padding: 18 }}>
                <EmptyState title={t('home.recent.empty.title')} body={t('home.recent.empty.body')} action={<Button kind="ghost" size="sm" onClick={() => navigate('orders')}>{t('home.recent.viewAll')}</Button>} />
              </div>
            ) : isMobile ? (
              <div style={{ display: 'grid' }}>
                {data.recentOrders.map(order => {
                  const orderLabel = order.externalOrderNumber || order.externalOrderId
                  const itemLabel = order.itemName || orderLabel
                  return (
                    <div key={order.id} style={{ display: 'grid', gap: 10, paddingBlock: 14, paddingInline: 16, borderTop: '1px solid var(--border-subtle)' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, alignItems: 'flex-start', minWidth: 0 }}>
                        <div style={{ fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--brand-700)', fontWeight: 700, ...TEXT_WRAP_STYLE }}>{orderLabel}</div>
                        <Pill tone={orderStatusTone(order.status)} size="sm">{t(`pages.orders.status.${order.status}`)}</Pill>
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: MOBILE_SINGLE_COLUMN, gap: 8 }}>
                        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', minWidth: 0 }}>
                          <ChannelPill name={PROVIDER_DISPLAY[order.provider]} size="sm" />
                          <span style={{ color: 'var(--fg)', fontWeight: 700, fontSize: 13, ...TEXT_WRAP_STYLE }}>{itemLabel}</span>
                        </div>
                        <div style={{ fontWeight: 800, color: 'var(--fg)', fontSize: 13 }}>{formatMoney(order.totalAmount ?? 0, locale, order.currency || currency)}</div>
                      </div>
                    </div>
                  )
                })}
              </div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                  <tbody>
                    {data.recentOrders.map(order => {
                    const orderLabel = order.externalOrderNumber || order.externalOrderId
                    const itemLabel = order.itemName || orderLabel
                    return (
                      <tr key={order.id} style={{ borderTop: '1px solid var(--border-subtle)' }}>
                        <td style={{ paddingBlock: 11, paddingInline: 18, fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--brand-700)', fontWeight: 600 }}>{orderLabel}</td>
                        <td style={{ paddingBlock: 11, paddingInline: 10 }}><ChannelPill name={PROVIDER_DISPLAY[order.provider]} size="sm" /></td>
                        <td style={{ paddingBlock: 11, paddingInline: 10, color: 'var(--fg)', fontWeight: 600, maxWidth: 230, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{itemLabel}</td>
                        <td style={{ paddingBlock: 11, paddingInline: 10, fontWeight: 800, color: 'var(--fg)' }}>{formatMoney(order.totalAmount ?? 0, locale, order.currency || currency)}</td>
                        <td style={{ paddingBlock: 11, paddingInline: 18 }}><Pill tone={orderStatusTone(order.status)} size="sm">{t(`pages.orders.status.${order.status}`)}</Pill></td>
                      </tr>
                    )
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        </div>

        <div style={{ display: 'grid', gap: 18 }}>
          <Card padding={22}>
            <SectionHeader title={t('home.channelHealth.title')} subtitle={t('home.channelHealth.realtime')} action={<Pill tone="brand" size="sm" dot>{t('home.channelHealth.live')}</Pill>} />
            {data.channels.length === 0 ? (
              <EmptyState title={t('home.channelHealth.empty.title')} body={t('home.channelHealth.empty.body')} action={<Button kind="ghost" size="sm" onClick={() => navigate('channels')}>{t('home.channelHealth.empty.cta')}</Button>} />
            ) : (
              <div style={{ display: 'grid', gap: 10 }}>
                {data.channels.map(channel => (
                  <div key={channel.channelConnectionId} style={{ display: 'grid', gridTemplateColumns: isMobile ? '32px minmax(0, 1fr)' : '32px minmax(0, 1fr) auto', gap: 10, alignItems: 'center', paddingBlock: 10, borderTop: '1px solid var(--border-subtle)' }}>
                    <ChannelDot name={PROVIDER_DISPLAY[channel.provider]} size={28} />
                    <div>
                      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                        <strong style={{ fontSize: 13, color: 'var(--fg)' }}>{channel.displayName}</strong>
                        <Pill tone={channelStatusTone(channel.status)} size="sm">{t(`channels.status.${channel.status}`)}</Pill>
                      </div>
                      <div style={{ marginTop: 3, color: 'var(--fg-subtle)', fontSize: 11.5 }}>
                        {t('home.channelHealth.ordersToday', { count: channel.ordersLast24h })} · {t('home.channelHealth.lastSync', { value: channel.lastSuccessfulPullAt ? new Intl.DateTimeFormat(numberLocale(locale), { dateStyle: 'medium', timeStyle: 'short' }).format(new Date(channel.lastSuccessfulPullAt)) : t('home.emptyValue') })}
                      </div>
                    </div>
                    <div style={{ textAlign: isMobile ? 'start' : 'end', color: 'var(--brand-900)', fontSize: 13, fontWeight: 800, gridColumn: isMobile ? '2' : undefined }}>
                      {t('home.channelHealth.revenueToday', { amount: formatMoneyTotals(channel.revenueLast24h, locale, currency) })}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>

          <Card padding={22}>
            <SectionHeader title={t('home.inventoryWatchlist.title')} subtitle={t('home.inventoryWatchlist.subtitle')} action={<Button kind="ghost" size="sm" onClick={() => navigate('inventory')}>{t('home.inventoryWatchlist.cta')}</Button>} />
            {data.inventoryWatchlist.length === 0 ? (
              <EmptyState title={t('home.inventoryWatchlist.empty.title')} body={t('home.inventoryWatchlist.empty.body')} />
            ) : (
              <div style={{ display: 'grid', gap: 12 }}>
                {data.inventoryWatchlist.map((item, index) => (
                  <button key={item.productId} type="button" aria-label={t('home.inventoryWatchlist.openProductAria', { name: item.name })} onClick={() => onOpenProduct?.(item.productId)} style={{ display: 'grid', gridTemplateColumns: '42px minmax(0, 1fr)', gap: 10, alignItems: 'center', border: '1px solid transparent', borderRadius: 14, background: 'transparent', paddingBlock: 6, paddingInline: 6, marginInline: -6, fontFamily: 'inherit', textAlign: 'start', cursor: onOpenProduct ? 'pointer' : 'default' }}>
                    <Thumb name={item.name} size={42} hue={index} />
                    <div style={{ minWidth: 0 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, alignItems: 'center' }}>
                        <strong style={{ color: 'var(--fg)', fontSize: 13, ...TEXT_WRAP_STYLE }}>{item.name}</strong>
                        <Pill tone={riskTone(item.risk)} size="sm">{t(`home.inventoryWatchlist.risk.${item.risk}`)}</Pill>
                      </div>
                      <div style={{ marginTop: 4, display: 'flex', gap: 8, flexWrap: 'wrap', color: 'var(--fg-subtle)', fontSize: 11.5 }}>
                        <span style={{ fontFamily: 'var(--font-mono)', ...TEXT_WRAP_STYLE }}>{item.sku}</span>
                        {item.listedProviders.map(provider => <ChannelPill key={provider} name={PROVIDER_DISPLAY[provider]} size="sm" />)}
                        <span>{t('home.inventoryWatchlist.available', { count: item.availableQty })}</span>
                        <span>{t('home.inventoryWatchlist.reserved', { count: item.committedQty })}</span>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </Card>

          <Card padding={22}>
            <SectionHeader title={t('home.quickActions.title')} subtitle={t('home.quickActions.subtitle')} />
            <div style={{ display: 'grid', gap: 8 }}>
              {[
                { label: t('home.quickActions.connectChannel'), desc: t('home.quickActions.connectChannelDesc'), icon: I.link, target: 'channels' as const },
                { label: t('home.quickActions.addProduct'), desc: t('home.quickActions.addProductDesc'), icon: I.plus, target: 'products' as const },
                { label: t('home.quickActions.reviewOrders'), desc: t('home.quickActions.reviewOrdersDesc'), icon: I.cart, target: 'orders' as const },
                { label: t('home.quickActions.inviteTeam'), desc: t('home.quickActions.inviteTeamDesc'), icon: I.users, target: 'team' as const },
              ].map(action => (
                <button key={action.target} type="button" onClick={() => navigate(action.target)} style={{ display: 'grid', gridTemplateColumns: '34px minmax(0, 1fr) 16px', alignItems: 'center', gap: 10, paddingBlock: 10, paddingInline: 12, borderRadius: 12, border: '1px solid var(--border-subtle)', background: 'var(--bg-card)', cursor: 'pointer', fontSize: 13, fontFamily: 'inherit', color: 'var(--fg)', textAlign: 'start' }}>
                  <span style={{ width: 34, height: 34, borderRadius: 10, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', background: 'var(--brand-100)', color: 'var(--brand-700)' }}>
                    <Icon size={16}>{action.icon}</Icon>
                  </span>
                  <span>
                    <strong style={{ display: 'block', fontSize: 13, color: 'var(--fg)' }}>{action.label}</strong>
                    <span style={{ display: 'block', marginTop: 2, fontSize: 11.5, color: 'var(--fg-subtle)' }}>{action.desc}</span>
                  </span>
                  <Icon size={15} color="var(--fg-subtle)">{I.arrowR}</Icon>
                </button>
              ))}
            </div>
          </Card>
        </div>
      </div>
    </div>
  )
}
